import type { PropsWithChildren, ReactNode } from "react";
import { ChevronRight } from "lucide-react";

import { buildSearchWithTrail } from "@/lib/navigationTrail";
import { resolvePageBreadcrumbLabel } from "@/lib/pageBreadcrumbLabel";
import { useNavigationTrail, usePageBreadcrumb } from "@/lib/navigationTrailContext";
import { cn } from "@/lib/utils";

export function HubBreadcrumbTitleRow({
  title,
  helpGlyph,
  className,
}: {
  title: ReactNode;
  helpGlyph?: ReactNode;
  className?: string;
}): JSX.Element {
  const { trail, navigateToTrailEntry } = useNavigationTrail();

  return (
    <nav aria-label="Breadcrumb" className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {trail.map((entry, index) => (
        <span key={`${entry.pathname}${entry.search}-${String(index)}`} className="inline-flex items-center gap-1.5">
          {index > 0 ? <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={2} /> : null}
          <a
            href={`${entry.pathname}${buildSearchWithTrail(entry.search, trail.slice(0, index))}`}
            className="text-sm font-medium text-brand-700 hover:text-brand-800"
            onClick={(event) => {
              if (
                event.button !== 0
                || event.metaKey
                || event.ctrlKey
                || event.shiftKey
                || event.altKey
              ) {
                return;
              }
              event.preventDefault();
              navigateToTrailEntry(index);
            }}
          >
            {entry.label}
          </a>
        </span>
      ))}
      <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={2} />
      <h1 aria-current="page" className="text-sm font-medium text-slate-900">
        {title}
      </h1>
      {helpGlyph}
    </nav>
  );
}

/**
 * Page `<h1>` that becomes `Ancestor › Title` on one line when an in-app navigation trail is active.
 */
export function HubPageTitle({
  children,
  breadcrumbLabel,
  className,
  titleClassName,
}: PropsWithChildren<{ breadcrumbLabel?: string; className?: string; titleClassName?: string }>): JSX.Element {
  const { trail } = useNavigationTrail();
  usePageBreadcrumb(resolvePageBreadcrumbLabel(breadcrumbLabel, children));

  if (trail.length > 0) {
    return <HubBreadcrumbTitleRow title={children} className={className} />;
  }

  return <h1 className={cn("text-xl font-semibold text-slate-900", titleClassName, className)}>{children}</h1>;
}
